import React, { useState, useEffect, useCallback } from 'react';
import { fetchDevice, controlLight, controlSwitch, setThermostat, readSensorData, updateDeviceSettings, configureDevice, controlIoOutput, startDeviceTrace, getDeviceTrace, stopDeviceTrace } from '../services/api';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Device } from '../types';
import { ui, getDeviceIcon, getStatusColor, getStatusLabel, getDeviceTypeLabel } from '../styles/shared';
import ErrorBanner from './shared/ErrorBanner';
import LightControl from './device-controls/LightControl';
import SwitchControl from './device-controls/SwitchControl';
import DimmerControl from './device-controls/DimmerControl';
import ThermostatControl from './device-controls/ThermostatControl';
import PowerMeterControl from './device-controls/PowerMeterControl';
import SensorControl from './device-controls/SensorControl';
import IoModuleControl from './device-controls/IoModuleControl';
import DeviceTracePanel from './device-controls/DeviceTracePanel';
import ZigbeeCapabilitiesPanel from './device-controls/ZigbeeCapabilitiesPanel';
import OfflineDetectionPanel from './device-controls/OfflineDetectionPanel';

// Stilar från den gemensamma stilfilen
const card = ui.card;

const DeviceControl: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isReadOnly } = useAuth();
    const deviceId = searchParams.get('deviceId') || 'default-device';
    const [device, setDevice] = useState<Device | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Device-specific state
    const [brightness, setBrightness] = useState<number>(255);
    const [isOn, setIsOn] = useState<boolean>(false);
    const [targetTemp, setTargetTemp] = useState<number>(22);
    const [sensorData, setSensorData] = useState<any>(null);
    // Settings state (soft local settings stored in DB)
    const [settingsForm, setSettingsForm] = useState<Record<string, string>>({});
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsMsg, setSettingsMsg] = useState<string | null>(null);
    // Zigbee OTA config state
    const [zigbeeConfigForm, setZigbeeConfigForm] = useState<Record<string, string>>({});
    const [zigbeeConfigSaving, setZigbeeConfigSaving] = useState(false);
    const [zigbeeConfigMsg, setZigbeeConfigMsg] = useState<string | null>(null);
    // Trace/debug capture state
    const [traceForm, setTraceForm] = useState<{ cluster: string; property: string; durationSeconds: string; maxEntries: string }>({
        cluster: '',
        property: '',
        durationSeconds: '300',
        maxEntries: '200',
    });
    const [traceLoading, setTraceLoading] = useState(false);
    const [traceSession, setTraceSession] = useState<any | null>(null);
    const [traceEntries, setTraceEntries] = useState<any[]>([]);
    const [traceMsg, setTraceMsg] = useState<string | null>(null);

    const loadDevice = useCallback(async () => {
        try {
            setLoading(true);
            const deviceData = await fetchDevice(deviceId);
            setDevice(deviceData);
            
            if (deviceData.brightness !== undefined) setBrightness(deviceData.brightness);
            if (deviceData.state != null) setIsOn(deviceData.state === 'ON');
            if (deviceData.targetTemperature !== undefined) setTargetTemp(deviceData.targetTemperature);
            if (deviceData.settings) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(deviceData.settings)) flat[k] = String(v);
                setSettingsForm(flat);
                setZigbeeConfigForm({
                    checkinInterval: flat.checkinInterval ?? '',
                    longPollInterval: flat.longPollInterval ?? '',
                });
            }
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Kunde inte hämta enheten');
        } finally {
            setLoading(false);
        }
    }, [deviceId]);

    useEffect(() => { loadDevice(); }, [loadDevice]);

    // Auto-load sensor data for read-only users (button is hidden for them)
    useEffect(() => {
        if (isReadOnly && device && ['temperature', 'humidity', 'motion', 'contact'].includes(device.type)) {
            handleLoadSensorData();
        }
    }, [isReadOnly, device?.type]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoadSensorData = async () => {
        try {
            const data = await readSensorData(deviceId);
            setSensorData(data);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Kunde inte läsa av givaren');
        }
    };

    const handleIoOutput = async (endpoint: number, state: 'ON' | 'OFF') => {
        try {
            await controlIoOutput(deviceId, endpoint, state);
            await loadDevice();
        } catch (err: any) {
            setError(err.message || 'Kunde inte styra IO-utgången');
        }
    };

    const handleStartTrace = async () => {
        try {
            setTraceLoading(true);
            setTraceMsg(null);
            const payload: Record<string, any> = {};
            if (traceForm.cluster.trim()) payload.cluster = traceForm.cluster.trim();
            if (traceForm.property.trim()) payload.property = traceForm.property.trim();
            if (traceForm.durationSeconds.trim()) payload.durationSeconds = Number(traceForm.durationSeconds);
            if (traceForm.maxEntries.trim()) payload.maxEntries = Number(traceForm.maxEntries);
            const res = await startDeviceTrace(deviceId, payload);
            setTraceSession(res?.session ?? null);
            setTraceEntries(res?.session?.entries ?? []);
            setTraceMsg('Spårningen är igång.');
        } catch (err: any) {
            setTraceMsg(`Fel: ${err.message}`);
        } finally {
            setTraceLoading(false);
        }
    };

    const handleRefreshTrace = async () => {
        try {
            setTraceLoading(true);
            setTraceMsg(null);
            const session = await getDeviceTrace(deviceId);
            setTraceSession(session);
            setTraceEntries(Array.isArray(session?.entries) ? session.entries : []);
        } catch (err: any) {
            setTraceSession(null);
            setTraceEntries([]);
            setTraceMsg(err.message?.includes('No active trace') ? 'Ingen spårning pågår.' : `Fel: ${err.message}`);
        } finally {
            setTraceLoading(false);
        }
    };

    const handleStopTrace = async () => {
        try {
            setTraceLoading(true);
            setTraceMsg(null);
            await stopDeviceTrace(deviceId);
            setTraceSession(null);
            setTraceEntries([]);
            setTraceMsg('Spårningen är stoppad.');
        } catch (err: any) {
            setTraceMsg(`Fel: ${err.message}`);
        } finally {
            setTraceLoading(false);
        }
    };

    useEffect(() => {
        if (!traceSession) return;
        const timer = setInterval(() => { handleRefreshTrace().catch(() => {}); }, 3000);
        return () => clearInterval(timer);
    }, [traceSession]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLightControl = async (state: 'ON' | 'OFF', newBrightness?: number) => {
        try {
            await controlLight(deviceId, state, newBrightness);
            // Optimistic update — don't re-fetch immediately; the zigbee device needs
            // time to process the command and report back before the API reflects the new state.
            setIsOn(state === 'ON');
            setDevice(prev => prev ? { ...prev, state } : prev);
            if (newBrightness !== undefined) setBrightness(newBrightness);
            setError(null);
            setTimeout(() => loadDevice(), 2000);
        } catch (err: any) {
            setError(err.message || 'Kunde inte styra lampan');
        }
    };

    const handleSwitchControl = async (state: 'ON' | 'OFF') => {
        try {
            await controlSwitch(deviceId, state);
            // Optimistic update — don't re-fetch immediately; the zigbee device needs
            // time to process the command and report back before the API reflects the new state.
            setIsOn(state === 'ON');
            setDevice(prev => prev ? { ...prev, state } : prev);
            setError(null);
            setTimeout(() => loadDevice(), 2000);
        } catch (err: any) {
            setError(err.message || 'Kunde inte styra strömbrytaren');
        }
    };

    const handleThermostatControl = async (temperature: number) => {
        try {
            await setThermostat(deviceId, temperature);
            setTargetTemp(temperature);
            setError(null);
            await loadDevice();
        } catch (err: any) {
            setError(err.message || 'Kunde inte styra termostaten');
        }
    };

    const handleSaveSettings = async (payload: Record<string, any>) => {
        setSettingsSaving(true);
        setSettingsMsg(null);
        try {
            await updateDeviceSettings(deviceId, payload);
            setSettingsMsg('Inställningarna är sparade.');
            await loadDevice();
        } catch (e: any) {
            setSettingsMsg(`Fel: ${e.message}`);
        } finally {
            setSettingsSaving(false);
        }
    };

    const handleApplyZigbeeConfig = async (attrs: Record<string, number>) => {
        if (Object.keys(attrs).length === 0) {
            setZigbeeConfigMsg('Fyll i minst ett värde.');
            return;
        }
        setZigbeeConfigSaving(true);
        setZigbeeConfigMsg(null);
        try {
            const result = await configureDevice(deviceId, {
                write: [{ cluster: 'genPollCtrl', attributes: attrs }],
            });
            const allOk = result.results?.every((r: any) => r.status === 'ok');
            const allQueued = result.results?.every((r: any) => r.status === 'queued');
            if (result.queued || allQueued) {
                await updateDeviceSettings(deviceId, attrs);
                setZigbeeConfigMsg('⏳ Enheten sover — inställningen är köad och skickas automatiskt nästa gång enheten vaknar.');
                await loadDevice();
            } else if (allOk) {
                await updateDeviceSettings(deviceId, attrs);
                setZigbeeConfigMsg('Inställningen är skickad till enheten.');
                await loadDevice();
            } else {
                const errors = result.results
                    ?.filter((r: any) => r.status === 'error')
                    .map((r: any) => r.error)
                    .join('; ');
                setZigbeeConfigMsg(`Delvis misslyckat: ${errors}`);
            }
        } catch (e: any) {
            setZigbeeConfigMsg(`Fel: ${e.message}`);
        } finally {
            setZigbeeConfigSaving(false);
        }
    };

    const renderDeviceControls = () => {
        if (!device) return null;
        switch (device.type) {
            case 'light':
                return <LightControl isOn={isOn} brightness={brightness} onBrightnessChange={setBrightness} onControl={handleLightControl} readOnly={isReadOnly} />;
            case 'switch':
                return <SwitchControl isOn={isOn} onControl={handleSwitchControl} readOnly={isReadOnly} />;
            case 'dimmer':
                return <DimmerControl brightness={brightness} onBrightnessChange={setBrightness} onApply={b => handleLightControl('ON', b)} readOnly={isReadOnly} />;
            case 'thermostat':
                return <ThermostatControl targetTemp={targetTemp} onTempChange={setTargetTemp} onApply={handleThermostatControl} readOnly={isReadOnly} />;
            case 'power_meter':
                return <PowerMeterControl device={device} sensorData={sensorData}
                    settingsForm={settingsForm} settingsSaving={settingsSaving} settingsMsg={settingsMsg}
                    zigbeeConfigForm={zigbeeConfigForm} zigbeeConfigSaving={zigbeeConfigSaving} zigbeeConfigMsg={zigbeeConfigMsg}
                    onLoadSensorData={handleLoadSensorData} onSettingsChange={setSettingsForm}
                    onSaveSettings={handleSaveSettings} onZigbeeConfigChange={setZigbeeConfigForm}
                    onApplyZigbeeConfig={handleApplyZigbeeConfig} />;
            case 'temperature': case 'humidity': case 'motion': case 'contact':
                return <SensorControl device={device} sensorData={sensorData}
                    settingsForm={settingsForm} settingsSaving={settingsSaving} settingsMsg={settingsMsg}
                    onLoadSensorData={handleLoadSensorData} onSettingsChange={setSettingsForm}
                    onSaveSettings={handleSaveSettings} readOnly={isReadOnly} />;
            case 'io_module':
                return <IoModuleControl device={device}
                    settingsForm={settingsForm} settingsSaving={settingsSaving} settingsMsg={settingsMsg}
                    onIoOutput={handleIoOutput} onSettingsChange={setSettingsForm}
                    onSaveSettings={handleSaveSettings} />;
            default:
                return (
                    <div style={card}>
                        <h3 style={{ marginTop: 0 }}>Okänd typ av enhet</h3>
                        <p>Det finns ingen styrning för enhetstypen {getDeviceTypeLabel(device.type)}.</p>
                    </div>
                );
        }
    };

    if (loading) return <div><p>Hämtar enheten…</p></div>;

    return (
        <div>
            {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

            <div style={{ maxWidth: '600px' }}>
                <h2>{getDeviceIcon(device?.type ?? '')} Styr enhet</h2>

                {device && (
                    <div style={{ ...card, marginBottom: '20px' }}>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Enhetens namn:</label>
                            <span style={{ fontSize: '18px' }}>{device.name}</span>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Enhets-ID:</label>
                            <code style={{ backgroundColor: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '14px', border: '1px solid #ddd' }}>{device.id}</code>
                        </div>
                        <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Typ:</label>
                            <span style={{ fontSize: '16px' }}>{getDeviceIcon(device.type)} {getDeviceTypeLabel(device.type)}</span>
                        </div>
                        <div>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Läge:</label>
                            <span style={{ fontSize: '16px', color: getStatusColor(device.status), fontWeight: 'bold' }}>● {getStatusLabel(device.status)}</span>
                        </div>
                    </div>
                )}

                {renderDeviceControls()}

                {/* Zigbee capabilities panel — shown only for coordinator-managed devices and non-read users */}
                {!isReadOnly && device?.coordinator_id && device?.ieee_address && (
                    <ZigbeeCapabilitiesPanel
                        deviceId={device.ieee_address}
                        isBattery={!!device.battery_type}
                    />
                )}

                {/* Offline-detection override — for ALL coordinator-managed devices, not just sensors */}
                {!isReadOnly && device?.coordinator_id && device?.ieee_address && (
                    <OfflineDetectionPanel
                        settingsForm={settingsForm} settingsSaving={settingsSaving} settingsMsg={settingsMsg}
                        onSettingsChange={setSettingsForm} onSaveSettings={handleSaveSettings}
                    />
                )}

                {!isReadOnly && (
                    <DeviceTracePanel
                        traceForm={traceForm} traceLoading={traceLoading}
                        traceSession={traceSession} traceEntries={traceEntries} traceMsg={traceMsg}
                        onFormChange={setTraceForm} onStart={handleStartTrace}
                        onRefresh={handleRefreshTrace} onStop={handleStopTrace}
                    />
                )}

                <div style={{ marginTop: '20px' }}>
                    <button onClick={() => navigate('/devices')}
                        style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                        ← Tillbaka till alla enheter
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeviceControl;
